import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import ImageUploader from "../../components/ImageUploader";

export default function BlogsAdmin() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", tags: "", cover_image: "", images: "", published: true });
  const [coverImages, setCoverImages] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  const load = () => api.get("/api/crm/blogs").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const open = (b) => {
    setEditing(b || null);
    setForm(b ? {
      title: b.title, excerpt: b.excerpt || "", content: b.content || "",
      tags: (b.tags || []).join(", "), cover_image: b.cover_image || "",
      images: (b.images || []).join(", "), published: b.published,
    } : { title: "", excerpt: "", content: "", tags: "", cover_image: "", images: "", published: true });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, excerpt: form.excerpt, content: form.content,
      cover_image: form.cover_image, published: form.published,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.patch(`/api/crm/blogs/${editing.id}`, payload);
      else await api.post("/api/crm/blogs", payload);
      toast.success("Saved");
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { await api.delete(`/api/crm/blogs/${id}`); load(); };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Journal CMS</div><h1 className="font-display text-4xl text-maroon">Blogs</h1></div>
        <button onClick={() => open(null)} className="btn-maroon" data-testid="add-blog-btn"><Plus size={14}/> New blog</button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((b) => (
          <div key={b.id} className="bg-white border border-line p-4 flex gap-3" data-testid={`blog-row-${b.id}`}>
            {b.cover_image && <img src={b.cover_image} alt="" className="w-24 h-24 object-cover"/>}
            <div className="flex-1">
              <div className="eyebrow text-[10px]">{(b.tags || []).join(" · ")}</div>
              <div className="font-display text-xl text-maroon">{b.title}</div>
              <div className="text-xs text-ash mt-1">{b.excerpt}</div>
              <div className="text-[10px] text-ash mt-2">{b.author_name} · {new Date(b.created_at).toLocaleDateString()} · {b.published ? "Published" : "Draft"}</div>
              <div className="mt-2 flex gap-3 text-xs">
                <button onClick={() => open(b)} className="text-maroon underline" data-testid={`edit-blog-${b.id}`}>Edit</button>
                <button onClick={() => remove(b.id)} className="text-maroon underline" data-testid={`delete-blog-${b.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white border border-line p-6 w-full max-w-3xl my-10 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl text-maroon">{editing ? "Edit blog" : "New blog"}</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18}/></button>
            </div>
            <input data-testid="blog-title" required placeholder="Title" className="w-full border border-line px-3 py-2 bg-pearl" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}/>
            <input data-testid="blog-excerpt" placeholder="Excerpt (1-2 lines)" className="w-full border border-line px-3 py-2 bg-pearl" value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})}/>
            <div><div className="eyebrow text-[10px] mb-2">Cover Image</div><ImageUploader value={coverImages} onChange={setCoverImages} multiple={false} maxFiles={1} kind="blog-cover" /></div>
            <div><div className="eyebrow text-[10px] mb-2">Gallery</div><ImageUploader value={galleryImages} onChange={setGalleryImages} multiple maxFiles={8} kind="blog" /></div>
            <input data-testid="blog-tags" placeholder="Tags (comma separated)" className="w-full border border-line px-3 py-2 bg-pearl" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})}/>
            <textarea data-testid="blog-content" required rows={10} placeholder="Write the blog body (HTML/Markdown supported)" className="w-full border border-line px-3 py-2 bg-pearl font-mono text-sm" value={form.content} onChange={(e) => setForm({...form, content: e.target.value})}/>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})}/> Publish</label>
            <button className="btn-maroon w-full justify-center" data-testid="submit-blog">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
